import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  applyThemeToDocument,
  DEFAULT_THEME_SLUG,
  getDocumentThemeSlug,
  getThemeConfig,
  getThemeSlug,
  THEMES_CONFIG
} from 'theme/themes'

const ThemeContext = createContext({
  theme: null,
  themeConfig: getThemeConfig(DEFAULT_THEME_SLUG),
  themes: THEMES_CONFIG,
  toggleTheme: () => {}
})

export const useTheme = () => useContext(ThemeContext)

export const ThemeProvider = ({ children }) => {
  // Only adopt a theme once it is present on the document (e.g. after login).
  // Avoids applying DEFAULT (purple) before the real theme is known.
  const [theme, setTheme] = useState(() => getDocumentThemeSlug())

  const setThemeById = useCallback((themeValue) => {
    setTheme(getThemeSlug(themeValue))
  }, [])

  useEffect(() => {
    if (!theme) return
    applyThemeToDocument(theme)
  }, [theme])

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined

    const syncThemeFromBodyClass = () => {
      const nextTheme = getDocumentThemeSlug()
      if (!nextTheme) return
      setTheme((currentTheme) => currentTheme === nextTheme ? currentTheme : nextTheme)
    }

    const observer = new MutationObserver(syncThemeFromBodyClass)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    })

    syncThemeFromBodyClass()

    return () => observer.disconnect()
  }, [])

  const themeConfig = useMemo(() => getThemeConfig(theme || DEFAULT_THEME_SLUG), [theme])

  const contextValue = useMemo(() => ({
    theme,
    themeConfig,
    themes: THEMES_CONFIG,
    toggleTheme: setThemeById,
    setThemeById
  }), [theme, themeConfig, setThemeById])

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}
